import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toCSV } from "@src/utils/fileConversion";
import { API_URL } from "@src/config/apiconfig";

// The COLUMNS expected from the backend and to be displayed.
export const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "product", label: "Product" },
  { key: "serial", label: "Serial" },
  { key: "invoice_number", label: "Invoice Number" },
  { key: "purchase_date", label: "Purchase Date" },
  { key: "pincode", label: "Pincode" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "consent", label: "Consent" },
  { key: "submitted_at", label: "Submitted At" },
];

export const COLUMNS_TO_SHOW = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "product", label: "Product" },
  { key: "invoice_number", label: "Invoice Number" },
];

const ROWS_PER_PAGE = 10;

/**
 * Trigger CSV download in the browser
 * @param {Array} data - The data to download
 * @param {string} filename - The filename for the downloaded file
 */
export function downloadExcel(data, filename = "admin_dashboard_data.csv") {
  const csv = toCSV(data, COLUMNS);
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  window.URL.revokeObjectURL(url);
}

export const useHomepageData = () => {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    consent: "",
    submitted_from: "",
    submitted_to: "",
  });

  const [minSubmittedDate, setMinSubmittedDate] = useState("");
  const [maxSubmittedDate, setMaxSubmittedDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setFetchError("");
      setDebugInfo(null);
      try {
        // const apiUrl = process.env.REACT_APP_BASE_URL;

        // Use enhanced fetch with debugging
        const response = await fetch(API_URL.GET_DASHBOARD_DATA);
        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        // console.log("Response : ", result);
        // console.log(
        //   "[AdminDashboard] Server responded with unexpected format:",
        //   result
        // );
        if (!result.success || !Array.isArray(result.data)) {
          throw new Error(
            result.error || "Invalid data format received from server"
          );
        }
        setTableData(result.data);

        // Find min/max for submitted_at (for period selection)
        let min = null,
          max = null;
        for (const row of result.data) {
          const val = row["submitted_at"];
          if (val) {
            const dateStr = val.slice(0, 10);
            if (!min || dateStr < min) min = dateStr;
            if (!max || dateStr > max) max = dateStr;
          }
        }
        setMinSubmittedDate(min);
        setMaxSubmittedDate(max);

        setDebugInfo(result.debug);
      } catch (error) {
        setFetchError(error.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // SEARCH & FILTER LOGIC
  const filterAndSearchData = () => {
    let filtered = tableData;

    // Only apply consent filter and submitted_at period; others are disabled/commented.
    filtered = filtered.filter((row) => {
      // Consent filter
      if (filters.consent) {
        if (filters.consent === "true") {
          if (
            !(
              row["consent"] === true ||
              row["consent"] === "true" ||
              row["consent"] === 1 ||
              row["consent"] === "1" ||
              row["consent"] === "yes"
            )
          )
            return false;
        } else if (filters.consent === "false") {
          if (
            !(
              row["consent"] === false ||
              row["consent"] === "false" ||
              row["consent"] === 0 ||
              row["consent"] === "0" ||
              row["consent"] === "no"
            )
          )
            return false;
        }
      }
      // submitted_at time period filter (between submitted_from and submitted_to, if set)
      const rowDate = row["submitted_at"]
        ? row["submitted_at"].slice(0, 10)
        : "";
      if (filters.submitted_from && rowDate < filters.submitted_from)
        return false;
      if (filters.submitted_to && rowDate > filters.submitted_to) return false;
      return true;
    });

    // Search term (applies to all COLUMNS, partial, insensitive)
    if (search.trim() !== "") {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter((row) =>
        COLUMNS.some((c) =>
          (row[c.key] ?? "").toString().toLowerCase().includes(s)
        )
      );
    }

    return filtered;
  };
  const visibleData = filterAndSearchData();

  // Pagination
  const totalPages = Math.max(1, Math.ceil(visibleData.length / ROWS_PER_PAGE));
  const paginatedData = visibleData.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Reset page if search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [visibleData, totalPages, currentPage]);

  // Pagination is now handled by Pagination component

  // Download with applied filters/search
  const handleDownloadFiltered = () => {
    downloadExcel(visibleData, "admin_dashboard_data.csv");
  };

  // Handler for filter dropdowns
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle logout
  const handleLogout = () => {
    // console.log("[homePageData] 🔓 Logging out — clearing session");
    localStorage.removeItem("isAdminAuthenticated");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  return {
    sidebarOpen,
    setSidebarOpen,
    sidebarCollapsed,
    setSidebarCollapsed,
    activeTab,
    setActiveTab,
    handleLogout,
    tableData,
    loading,
    fetchError,
    debugInfo,
    minSubmittedDate,
    maxSubmittedDate,
    filters,
    search,
    setSearch,
    currentPage,
    visibleData,
    paginatedData,
    totalPages,
    handlePageChange,
    handleFilterChange,
    handleDownloadFiltered,
  };
};
